'use client';

import { useEffect, useMemo, useState } from 'react';
import { Info, Mail } from 'lucide-react';
import { ErrorMessage } from '@/hooks/useHandleError';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import { genderList } from '@/constants/constants';
import {
    FestaEntryNoteItem,
    FestaEntryOrganizationItem,
    FestaEntryViewItem,
    UserFestaEntryFormData,
} from '@/types/festaEntries';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import ErrorBox from '@/components/ErrorBox';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import LabelBadge from '@/components/LabelBadge';
import DashboardPersonalEmailAddress from '@/components/features/dashboard/modal/DashboardPersonalEmailAddress';
import FormSelect from '@/components/ui/form/FormSelect';
import { Birthdate, FormSelectBirthdate } from '@/components/ui/form/FormSelectBirthdate';
import PrivacyPolicy from '@/(routers)/(entry)/festa-entry/components/modal/PrivacyPolicy';
import FestaEntryInfo from '@/(routers)/(entry)/festa-entry/components/unit/FestaEntryInfo';

export interface FestaEntryFormUserInfoProps {
    formData: UserFestaEntryFormData;
    setFormData: React.Dispatch<React.SetStateAction<UserFestaEntryFormData>>;
    birthdate: Birthdate;
    setBirthdate: (b: Birthdate) => void;
    onBack: () => void;
    itemStart: number;
    festaEntryNoteList: FestaEntryNoteItem[];
    festaEntryOrganizationList: FestaEntryOrganizationItem[];
    onSubmit: () => void;
    isProcessing: boolean;
    isDeepLinkedStart: boolean;
    validationRef: React.RefObject<HTMLDivElement | null>;
    errorMessage: ErrorMessage | null;
}
export default function FestaEntryFormUserInfo({
    formData,
    setFormData,
    birthdate,
    setBirthdate,
    onBack,
    itemStart,
    festaEntryNoteList,
    festaEntryOrganizationList,
    onSubmit,
    isProcessing,
    isDeepLinkedStart,
    validationRef,
    errorMessage,
}: FestaEntryFormUserInfoProps) {
    const { openModal } = useModalStore();
    const userAuth = useAuthStore((s) => s.userAuth);
    const [isPrivacyPolicyAccepted, setIsPrivacyPolicyAccepted] = useState(false);
    const isBirthdateComplete = !!(birthdate.year && birthdate.month && birthdate.day);
    const birthdateSelectClass = `select-form ${isBirthdateComplete ? 'text-color-base' : 'text-color-base-1-dark'}`;

    const festaInfo: FestaEntryViewItem = useMemo(
        () => ({
            festa_entry_name: formData.festa_entry_name || '',
            schedule_labels: formData.schedule_labels || [],
            location_name: formData.location_name || '',
            festa_entry_type_name: formData.festa_entry_type_name || '',
            contact_email: formData.festa_entry_contact_email || '',
            contact_name: formData.festa_entry_contact_name || '',
            login_required: formData.festa_entry_type_is_login_required === 1,
        }),
        [formData],
    );

    useEffect(() => {
        if (userAuth?.email) {
            setFormData((prev) => ({
                ...prev,
                email: userAuth.email || '',
            }));
        }
    }, [userAuth?.email, setFormData]);

    const handleBirthdateChange = (b: Birthdate) => {
        setBirthdate(b);
        if (b.year && b.month && b.day) {
            setFormData((prev) => ({
                ...prev,
                birthday: `${b.year}${b.month}${b.day}`,
            }));
        } else {
            setFormData((prev) => ({ ...prev, birthday: '' }));
        }
    };

    const handleIsPrivacyPolicyAcceptedToggle = () => {
        setIsPrivacyPolicyAccepted((prev) => !prev);
    };

    return (
        <>
            <div className="flex flex-col justify-start items-center w-[80%] pb-8">
                <FestaEntryInfo
                    festaEntryName={festaInfo?.festa_entry_name}
                    scheduleLabels={festaInfo?.schedule_labels}
                    locationName={festaInfo?.location_name}
                    entryTypeLabel="参加方法"
                    entryTypeValue={festaInfo?.festa_entry_type_name}
                />
            </div>
            <div className="flex flex-col w-full xl:w-[60%] pb-12">
                <div className="flex flex-col w-[80%] lg:w-[800px] mx-auto">
                    <div className="flex flex-col gap-6 mb-8">
                        {festaEntryNoteList.map((note, index) => (
                            <div key={index} className="flex flex-col">
                                <div className="font-bold text-base xl:text-lg pb-2">{note.title}</div>
                                <div className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">
                                    {note.description}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-col">
                        <div className="font-bold text-base xl:text-lg pb-2">問い合わせ先</div>
                        <div className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">
                            {festaInfo.contact_email}
                        </div>
                        <div className="text-sm text-gray-600 whitespace-pre-line leading-relaxed">
                            {festaInfo.contact_name}
                        </div>
                    </div>
                </div>
            </div>
            <div ref={validationRef} className="flex flex-col justify-start items-center w-full xl:w-[60%] pb-8">
                <div className="flex flex-col justify-start items-center pt-4 pb-1 w-full">
                    <div className="flex w-[80%] lg:w-[800px]">
                        <ErrorBox message={errorMessage?.message} errors={errorMessage?.errors} />
                    </div>
                    <div className="flex flex-col justify-start items-center pt-1 pb-1 w-full">
                        <div className="flex justify-start w-[80%] lg:w-[800px]">
                            <div className="flex flex-col justify-center items-center">
                                <div className="flex items-center pb-2">
                                    <ImageAdaptive
                                        src={`/images/common/item${itemStart++}.svg`}
                                        baseWidth={50}
                                        breakpoints={{ md: 60 }}
                                    />
                                </div>
                                <div className="flex-grow border-l-2 border-color-base-2 w-0"></div>
                            </div>
                            <div className="px-2"></div>
                            <div className="flex flex-col justify-start items-start font-bold text-base xl:text-xl w-full">
                                <div className="flex items-center pt-2 md:pt-3">
                                    <div className="flex text-color-base-2">申込者情報を入力してください</div>
                                </div>
                                <div className="flex flex-col w-full pt-8 pb-12">
                                    {festaInfo.login_required && (
                                        <>
                                            <div className="flex py-1">
                                                <div className="font-medium text-sm text-color-base pr-2">
                                                    スクーマーズID
                                                </div>
                                            </div>
                                            <div className="flex w-full">
                                                <div className="p-4">@{userAuth?.schoomyersId}</div>
                                            </div>
                                            <div className="pt-8"></div>
                                        </>
                                    )}
                                    <div className="flex items-center py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">学校名（所属）</div>
                                        <LabelBadge type="required" />
                                    </div>
                                    <div className="flex items-center pr-2 py-1">
                                        <IconAdaptive
                                            Icon={Info}
                                            strokeWidth={2.0}
                                            baseSize={18}
                                            breakpoints={{ md: 20 }}
                                            className="mr-[4px] text-color-co-gray-dark"
                                        />
                                        <div className="font-medium text-[10px] md:text-sm text-color-co-gray-dark pl-1">
                                            選択肢にない場合は「その他」を選択し、学校名を直接入力してください。
                                        </div>
                                    </div>
                                    <div className="flex w-full">
                                        <select
                                            id="entry_organization"
                                            name="entry_organization"
                                            value={formData.entry_organization_cd ?? ''}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    entry_organization_cd: e.target.value,
                                                    entry_organization_name: '',
                                                })
                                            }
                                            className={`select-form p-4 
                                            ${
                                                formData.entry_organization_cd === ''
                                                    ? 'text-color-base-1-dark'
                                                    : 'text-color-base'
                                            }
                                            `}
                                        >
                                            <option value="">選択してください</option>
                                            {festaEntryOrganizationList.map((org) => (
                                                <option key={org.cd} value={org.cd}>
                                                    {org.cd} | {org.name}
                                                </option>
                                            ))}{' '}
                                            <option value="other">その他</option>
                                        </select>
                                    </div>
                                    <div className="pt-2"></div>
                                    {formData.entry_organization_cd === 'other' && (
                                        <>
                                            <div className="flex w-full">
                                                <input
                                                    type="text"
                                                    id="entry_organization_name"
                                                    name="entry_organization_name"
                                                    placeholder="スクーミー高等学校"
                                                    inputMode="text"
                                                    maxLength={32}
                                                    value={formData.entry_organization_name || ''}
                                                    onChange={(e) =>
                                                        setFormData({
                                                            ...formData,
                                                            entry_organization_name: e.target.value,
                                                        })
                                                    }
                                                    className="input-form p-4"
                                                />
                                            </div>
                                            <div className="pt-8"></div>
                                        </>
                                    )}
                                    <div className="flex items-center py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">お名前</div>
                                        <LabelBadge type="required" />
                                    </div>
                                    <div className="flex w-full">
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            placeholder="武田一郎"
                                            inputMode="text"
                                            maxLength={20}
                                            value={formData.name}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    name: e.target.value,
                                                })
                                            }
                                            className="input-form p-4"
                                        />
                                    </div>
                                    <div className="pt-8"></div>
                                    <div className="flex items-center py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">フリガナ</div>
                                        <LabelBadge type="required" />
                                    </div>
                                    <div className="flex w-full">
                                        <input
                                            type="text"
                                            id="name_kana"
                                            name="name_kana"
                                            placeholder="タケダイチロウ"
                                            inputMode="text"
                                            maxLength={20}
                                            value={formData.name_kana}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    name_kana: e.target.value,
                                                })
                                            }
                                            className="input-form p-4"
                                        />
                                    </div>
                                    <div className="pt-8"></div>
                                    <div className="flex items-center py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">メールアドレス</div>
                                        <LabelBadge type="required" />
                                    </div>
                                    <div className="flex items-center pr-2 py-1">
                                        <IconAdaptive
                                            Icon={Info}
                                            strokeWidth={2.0}
                                            baseSize={18}
                                            breakpoints={{ md: 20 }}
                                            className="mr-[4px] text-color-co-gray-dark"
                                        />
                                        <div className="font-medium text-[10px] md:text-sm text-color-co-gray-dark pl-1">
                                            学校のメールアドレスでは受信できない場合があります。個人のメールアドレスでのご登録をお勧めします。
                                        </div>
                                    </div>
                                    {festaInfo.login_required ? (
                                        <div className="flex w-full pt-2">
                                            {userAuth?.email ? (
                                                <div className="p-4">{userAuth?.email}</div>
                                            ) : (
                                                <ButtonGAdaptive
                                                    type="button"
                                                    icon={Mail}
                                                    label="メールアドレスを登録する"
                                                    description="Register your email address"
                                                    size="xs"
                                                    breakpoints={{ md: 'xl' }}
                                                    onClick={() =>
                                                        openModal({
                                                            id: 'entry-form-email',
                                                            component: <DashboardPersonalEmailAddress />,
                                                            size: {
                                                                pcWidth: '900px',
                                                                mbWidth: '90%',
                                                                pcHeight: '520px',
                                                                mbHeight: '80%',
                                                            },
                                                            direction: 'clip',
                                                            overFlowType: 'auto',
                                                        })
                                                    }
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex w-full">
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                placeholder="go@schoomy.com"
                                                inputMode="text"
                                                maxLength={254}
                                                value={formData.email}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        email: e.target.value,
                                                    })
                                                }
                                                className="input-form p-4"
                                            />
                                        </div>
                                    )}
                                    <div className="pt-8"></div>
                                    <div className="flex items-center py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">電話番号</div>
                                        <LabelBadge type="required" />
                                    </div>
                                    <div className="flex w-full">
                                        <input
                                            type="tel"
                                            id="phone_number"
                                            name="phone_number"
                                            placeholder="08012345678"
                                            inputMode="numeric"
                                            maxLength={11}
                                            value={formData.phone_number}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    phone_number: e.target.value,
                                                })
                                            }
                                            className="input-form p-4"
                                        />
                                    </div>
                                    <div className="pt-8"></div>
                                    <div className="flex items-center py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">生年月日</div>
                                        <LabelBadge type="required" />
                                    </div>
                                    <div className="flex w-full">
                                        <FormSelectBirthdate
                                            value={birthdate}
                                            onChange={handleBirthdateChange}
                                            className={birthdateSelectClass}
                                        />
                                    </div>
                                    <div className="pt-8"></div>
                                    <div className="flex items-center py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">性別</div>
                                        <LabelBadge type="required" />
                                    </div>
                                    <div className="flex w-full">
                                        <FormSelect
                                            id="gender"
                                            name="gender"
                                            value={formData.gender_cd || ''}
                                            onChange={(e) =>
                                                setFormData({
                                                    ...formData,
                                                    gender_cd: e.target.value,
                                                })
                                            }
                                            options={[{ label: '性別を選択してください', value: '' }, ...genderList]}
                                            className={`select-form w-1/3
                                            ${
                                                formData.gender_cd === '' ? 'text-color-base-1-dark' : 'text-color-base'
                                            }`}
                                        />
                                    </div>
                                    <div className="pt-8"></div>
                                    <div className="flex items-center py-1">
                                        <div className="font-medium text-sm text-color-base pr-2">自由記述欄</div>
                                        <LabelBadge type="optional" />
                                    </div>
                                    <div className="flex flex-col w-full">
                                        <div className="flex w-full">
                                            <textarea
                                                id="remarks"
                                                name="remarks"
                                                placeholder="連絡事項やご質問があればご記入ください"
                                                inputMode="text"
                                                value={formData.remarks}
                                                onChange={(e) =>
                                                    setFormData({
                                                        ...formData,
                                                        remarks: e.target.value,
                                                    })
                                                }
                                                className="textarea-form p-4"
                                                rows={10}
                                            />
                                        </div>
                                        <div
                                            className={`flex justify-end font-montserrat font-medium text-xs pt-1 w-full ${
                                                (formData.remarks?.length ?? 0) > 600
                                                    ? 'text-color-error'
                                                    : 'text-color-base'
                                            }`}
                                        >
                                            {`${formData.remarks?.length ?? 0} / ${600}`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-left px-4 pb-8">
                <div className="flex items-center pb-2">
                    <label className="relative inline-flex items-center cursor-pointer">
                        <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={isPrivacyPolicyAccepted}
                            onChange={handleIsPrivacyPolicyAcceptedToggle}
                        />
                        <div
                            className="w-10 h-6 rounded-full peer bg-gray-200 
                            peer-checked:after:translate-x-4 peer-checked:after:border-white 
                            after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-color-white-base after:border-gray-300
                            after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-color-base-2"
                        />
                    </label>
                    <div className="flex items-center text-xs text-gray-400 pl-2">下記内容に同意します。</div>
                </div>
                <ul className="list-disc pl-5 text-xs text-gray-400 pb-8 space-y-2">
                    <li>
                        <div
                            className="cursor-pointer underline"
                            onClick={() =>
                                openModal({
                                    id: 'apply-form-privacy-policy',
                                    component: <PrivacyPolicy />,
                                    size: {
                                        pcWidth: '800px',
                                        mbWidth: '90%',
                                        pcHeight: '620px',
                                        mbHeight: '80%',
                                    },
                                    direction: 'clip',
                                    overFlowType: 'auto',
                                })
                            }
                        >
                            プライバシーポリシー
                        </div>
                    </li>
                    <li className="space-y-1">
                        <p>スクーミーのイベントでは記録・広報のため写真・動画撮影を行う場合があります。</p>
                        <p>撮影した内容は当社Webサイト・SNS・印刷物等で使用する場合があります。</p>
                        <p>掲載が難しい場合は、事前または当日に担当スタッフまでお知らせください。</p>
                    </li>
                </ul>
            </div>
            <div className="flex justify-center w-full pb-20">
                <div className="flex-grow"></div>
                {!isDeepLinkedStart && (
                    <div
                        className="flex justify-end xl:justify-center font-medium text-sm w-[40%] xl:w-[18%] px-2 lx:px-4"
                        onClick={onBack}
                    >
                        <ImageAdaptive src="/images/inquiry/back.svg" baseWidth={30} breakpoints={{ md: 30 }} />
                        <button className="m-2 py-2 bg-color-white-base font-notojp font-bold text-xs xl:text-sm text-gray-500 whitespace-nowrap">
                            破棄して戻る
                        </button>
                    </div>
                )}
                <div
                    className={`flex w-[40%] xl:w-[18%] px-2 lx:px-4 ${
                        isDeepLinkedStart ? 'justify-center' : 'justify-start xl:justify-center'
                    }`}
                >
                    {isPrivacyPolicyAccepted ? (
                        <>
                            {!isProcessing ? (
                                <button
                                    className="py-2 xl:py-4 px-8 xl:px-28 bg-color-base-2 font-notojp font-bold text-sm xl:text-base text-color-white-base rounded-3xl shadow-right-bottom hover:opacity-90 whitespace-nowrap"
                                    onClick={onSubmit}
                                >
                                    申し込む
                                </button>
                            ) : (
                                <ImageAdaptive
                                    src="/images/common/loading.svg"
                                    baseWidth={40}
                                    breakpoints={{ md: 50 }}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <button className="py-2 xl:py-4 px-8 xl:px-28 bg-gray-300 font-notojp font-bold text-sm xl:text-base text-color-white-base rounded-3xl shadow-right-bottom whitespace-nowrap cursor-default">
                                申し込む
                            </button>
                        </>
                    )}
                </div>
                <div className="flex-grow"></div>
            </div>
        </>
    );
}
